import { useEffect, useRef, useState } from 'react';
import Chart from './Chart';

// Real WBTC price history, routed through /belpay-api (see src/setupProxy.js) since
// belpay.io sends no CORS headers. History covers the last 24h; live polling then
// appends fresh real ticks on top so the chart keeps moving while the page is open.
const POLL_MS = 10000;
const HISTORY_HOURS = 24;
const MAX_POINTS = 300;
const CHART_WIDTH = 144;

function formatDate(date) {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

export default function WbtcLiveChart({ height = 100 }) {
  const [points, setPoints] = useState([]);
  const pointsRef = useRef([]);
  const historyLoadedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const appendLive = (price, timeSec) => {
      const next = [...pointsRef.current, { time: timeSec, value: price }].slice(-MAX_POINTS);
      pointsRef.current = next;
      setPoints(next);
    };

    const loadHistory = () => {
      const to = new Date();
      const from = new Date(to.getTime() - HISTORY_HOURS * 60 * 60 * 1000);
      fetch(`/belpay-api/wbtc-market-rates/from/${formatDate(from)}/to/${formatDate(to)}`)
        .then((res) => res.json())
        .then((res) => {
          if (cancelled || !Array.isArray(res?.data)) return;
          const history = res.data
            .map((row) => ({
              time: Math.floor(new Date(row.created_at.replace(' ', 'T') + 'Z').getTime() / 1000),
              value: Number(row.rate),
            }))
            .filter((p) => Number.isFinite(p.time) && Number.isFinite(p.value))
            .sort((a, b) => a.time - b.time);
          // De-dupe same-second entries (the API sometimes repeats a row back-to-back).
          const deduped = history.filter((p, i) => i === 0 || p.time !== history[i - 1].time);
          pointsRef.current = deduped;
          setPoints(deduped);
        })
        .catch(() => {})
        .finally(() => {
          historyLoadedRef.current = true;
        });
    };

    const poll = () => {
      fetch('/belpay-api/live-rate/WBTC')
        .then((res) => res.json())
        .then((data) => {
          if (cancelled) return;
          const price = Number(data.in_USD);
          if (!Number.isFinite(price)) return;
          const now = Math.floor(Date.now() / 1000);
          if (pointsRef.current.length && now <= pointsRef.current[pointsRef.current.length - 1].time) return;
          appendLive(price, now);
        })
        .catch(() => {});
    };

    loadHistory();
    const id = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (points.length === 0) {
    return (
      <div className="live-rate-badge" style={{ width: CHART_WIDTH, marginLeft: 'auto' }}>
        <span className="live-dot"></span>Live Rate
      </div>
    );
  }

  const isUp = points.length > 1 && points[points.length - 1].value >= points[0].value;
  const lineColor = isUp ? 'green' : '#ff0000';
  const colors = {
    lineColor,
    areaTopColor: isUp ? 'rgba(0, 128, 0, 0.4)' : 'rgba(255, 0, 0, 0.4)',
    areaBottomColor: isUp ? 'rgba(0, 128, 0, 0.04)' : 'rgba(255, 0, 0, 0.04)',
  };

  return (
    <div style={{ width: CHART_WIDTH, height, marginLeft: 'auto' }}>
      <Chart data={points} height={height} colors={colors} />
    </div>
  );
}
